import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    FiChevronRight,
    FiHome,
    FiTool,
    FiCpu,
    FiCode,
    FiBook,
    FiHelpCircle,
    FiInfo,
    FiMail,
    FiShield,
    FiFileText,
} from "react-icons/fi";
import { useLanguage } from "../../contexts/LanguageContext";

interface SidebarItem {
    id: string;
    label: string;
    href?: string;
    icon: React.ComponentType<any>;
    children?: SidebarItem[];
}

const Sidebar = () => {
    const { t } = useLanguage();
    const location = useLocation();
    const [expandedItems, setExpandedItems] = useState<string[]>(["tools"]);

    const sidebarItems: SidebarItem[] = [
        {
            id: "home",
            label: t("nav.home"),
            href: "/",
            icon: FiHome,
        },
        {
            id: "tools",
            label: t("nav.tools"),
            icon: FiTool,
            children: [
                {
                    id: "tools-overview",
                    label: t("sidebar.toolsOverview"),
                    href: "/tools",
                    icon: FiTool,
                },
                {
                    id: "minddeck",
                    label: t("nav.minddeck"),
                    href: "/tools/minddeck",
                    icon: FiCpu,
                },
                {
                    id: "jsonparser",
                    label: t("nav.jsonparser"),
                    href: "/tools/json-parser",
                    icon: FiCode,
                },
            ],
        },
        {
            id: "guide",
            label: t("nav.guide"),
            href: "/guide",
            icon: FiBook,
        },
        {
            id: "support",
            label: t("footer.support"),
            icon: FiHelpCircle,
            children: [
                // {
                //     id: "faq",
                //     label: t("nav.faq"),
                //     href: "/faq",
                //     icon: FiHelpCircle,
                // },
                {
                    id: "contact",
                    label: t("nav.contact"),
                    href: "/contact",
                    icon: FiMail,
                },
            ],
        },
        {
            id: "company",
            label: t("sidebar.companyInfo"),
            icon: FiInfo,
            children: [
                {
                    id: "about",
                    label: t("nav.about"),
                    href: "/about",
                    icon: FiInfo,
                },
                {
                    id: "privacy",
                    label: t("nav.privacy"),
                    href: "/privacy",
                    icon: FiShield,
                },
                {
                    id: "terms",
                    label: t("nav.terms"),
                    href: "/terms",
                    icon: FiFileText,
                },
                // {
                //     id: "changelog",
                //     label: t("nav.changelog"),
                //     href: "/changelog",
                //     icon: FiClock,
                // },
                // {
                //     id: "roadmap",
                //     label: t("nav.roadmap"),
                //     href: "/roadmap",
                //     icon: FiMap,
                // },
            ],
        },
    ];

    const toggleExpanded = (itemId: string) => {
        setExpandedItems((prev) =>
            prev.includes(itemId)
                ? prev.filter((id) => id !== itemId)
                : [...prev, itemId]
        );
    };

    const isActive = (href: string) => location.pathname === href;
    const isExpanded = (itemId: string) => expandedItems.includes(itemId);

    const renderSidebarItem = (item: SidebarItem, level = 0) => {
        const hasChildren = item.children && item.children.length > 0;
        const expanded = isExpanded(item.id);
        const active = item.href ? isActive(item.href) : false;

        return (
            <div key={item.id}>
                {hasChildren ? (
                    <button
                        onClick={() => toggleExpanded(item.id)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                            level === 0
                                ? "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                                : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                        }`}
                        style={{ paddingLeft: `${0.75 + level * 1}rem` }}
                    >
                        <div className="flex items-center space-x-3">
                            <item.icon className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{item.label}</span>
                        </div>
                        <motion.div
                            animate={{ rotate: expanded ? 90 : 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <FiChevronRight className="w-4 h-4 flex-shrink-0" />
                        </motion.div>
                    </button>
                ) : (
                    <Link
                        to={item.href!}
                        className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                            active
                                ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg"
                                : level === 0
                                ? "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                                : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                        }`}
                        style={{ paddingLeft: `${0.75 + level * 1}rem` }}
                    >
                        <item.icon className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{item.label}</span>
                    </Link>
                )}

                <AnimatePresence>
                    {hasChildren && expanded && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="mt-1 space-y-1"
                        >
                            {item.children!.map((child) =>
                                renderSidebarItem(child, level + 1)
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        );
    };

    return (
        <aside className="hidden lg:block w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="p-4">
                {/* Navigation */}
                <nav className="space-y-1">
                    {sidebarItems.map((item) => renderSidebarItem(item))}
                </nav>

                {/* Quick Actions */}
                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                        빠른 링크
                    </h3>
                    <div className="space-y-2">
                        <a
                            href="https://minddeck.mewseeds.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-2 px-3 py-2 text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                        >
                            <FiCpu className="w-4 h-4" />
                            <span>MindDeck 바로가기</span>
                        </a>
                        <a
                            href="https://json.mewseeds.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-2 px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        >
                            <FiCode className="w-4 h-4" />
                            <span>JSON Parser 바로가기</span>
                        </a>
                    </div>
                </div>
            </div>
        </aside>
    );
};

export default Sidebar;
